import type { ModalFooterOnClickHandler } from 'LuxKit/Modal/ModalFooter'
import type { PropsWithChildren } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formToObject } from 'lib/forms/formToObject'
import FlightsDatesModalFooter from '../FlightsDatePicker/FlightsDatesModalFooter'
import { CalendarWeekdayHeader } from 'components/Common/Calendar/BasicCalendar'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { MODAL_BODY_GRID_AREA, MODAL_FOOTER_GRID_AREA, MODAL_HEADER_GRID_AREA } from 'LuxKit/Modal/ModalSheet'

const WeekdayRow = styled(CSSBreakpoint)`
  grid-area: weekdays;
  padding: ${rem(8)} ${rem(20)};
`

const SearchMenuForm = styled.form`
  ${mediaQueryDown.mobile} {
    &&:has(> ${WeekdayRow}) {
      grid-template-areas:
        '${MODAL_HEADER_GRID_AREA}'
        'weekdays'
        '${MODAL_BODY_GRID_AREA}'
        '${MODAL_FOOTER_GRID_AREA}';
      grid-template-rows: [first] min-content min-content minmax(0, 1fr) min-content [last];
    }
  }
`

interface Props {
  title: string
  showDates?: boolean
  showTravellers?: boolean
  useApplyActions?: boolean
}

function FlightSearchMenuMobile(props: PropsWithChildren<Props>) {
  const {
    title,
    children,
    showDates,
    showTravellers,
    useApplyActions,
  } = props

  const intl = useIntl()
  const { resolve } = useModalElementContext<any>()
  const { occupants } = useContext(FlightSearchWidgetStateContext)
  const travellerLabel = useMemo(() => formatOccupantsShort(occupants), [occupants])

  const continueText = intl.formatMessage({
    id: 'TNIFgxH6QvWO2MwOnp73',
    defaultMessage: 'Continue',
    description: 'Button text to continue in flight search mobile menu',
  })

  const applyText = intl.formatMessage({
    id: 'qjqe3TY2rAei5vA37jN_',
    defaultMessage: 'Apply',
    description: 'Button text to apply flight date selection changes',
  })

  const cancelText = intl.formatMessage({
    id: 'F-YTO1e90A3DqzfDiCrU',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel flight date selection in the flights flow',
  })

  const handleCancel = useCallback<ModalFooterOnClickHandler>((_event, resolve) => {
    resolve()
  }, [])

  const primaryActionProps = useMemo(() => ({
    children: useApplyActions ? applyText : continueText,
    type: 'submit' as const,
  }), [applyText, continueText, useApplyActions])

  const secondaryActionProps = useMemo(() => useApplyActions ? {
    children: cancelText,
    type: 'button' as const,
    onClick: handleCancel,
  } : undefined, [cancelText, handleCancel, useApplyActions])

  return (
    <ModalBase mode="drawer">
      <SearchMenuForm onSubmit={e => {
        e.preventDefault()
        const values = formToObject(e.currentTarget)
        resolve(values)
      }}
      >
        <ModalHeader title={title} />
        {showDates && useApplyActions && <WeekdayRow max="mobile" dynamic>
          <CalendarWeekdayHeader />
        </WeekdayRow>}
        <ModalBody>
          <ModalContent>
            {children}
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={primaryActionProps}
          secondaryActionProps={secondaryActionProps}
        >
          {showDates && <FlightsDatesModalFooter />}
          {showTravellers && travellerLabel && (
            <BodyText variant="medium" weight="bold" align="center">
              {travellerLabel}
            </BodyText>
          )}
        </ModalFooter>
      </SearchMenuForm>
    </ModalBase>
  )
}

export default FlightSearchMenuMobile
